import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import type { Paginated } from '@fdt/shared';
import type { ListParams } from '../api/resource';

export interface ListOptions {
  /** Extra filters merged into every list request. */
  filters?: Record<string, string | number | boolean | undefined>;
  defaultSortBy?: string;
  pageSize?: number;
}

/**
 * Paging, search and sort state for one paginated endpoint.
 *
 * Split out of `useCrud` because documents and payments are not editable rows:
 * they are posted and cancelled, never updated or deleted, so they have no
 * `update`/`remove` to satisfy the `Resource` contract. Casting the api to
 * `never` to borrow `useCrud` silently erased the row type and left the table
 * columns typed `unknown`.
 */
export function useList<TItem>(
  key: string,
  fetchList: (params: ListParams) => Promise<Paginated<TItem>>,
  options: ListOptions = {},
) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(options.pageSize ?? 25);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState(options.defaultSortBy);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const params = useMemo<ListParams>(
    () => ({
      page,
      pageSize,
      search: search.trim() || undefined,
      sortBy,
      sortDir,
      ...options.filters,
    }),
    [page, pageSize, search, sortBy, sortDir, options.filters],
  );

  const list = useQuery({
    queryKey: [key, params],
    queryFn: () => fetchList(params),
    placeholderData: (previous) => previous,
  });

  const toggleSort = useCallback(
    (field: string) => {
      setSortBy((current) => {
        setSortDir(current === field && sortDir === 'asc' ? 'desc' : 'asc');
        return field;
      });
    },
    [sortDir],
  );

  const applySearch = useCallback((value: string) => {
    setSearch(value);
    // A new search must not land the user on a page that no longer exists.
    setPage(1);
  }, []);

  return {
    list,
    params,
    page,
    setPage,
    pageSize,
    setPageSize,
    search,
    setSearch: applySearch,
    sortBy,
    sortDir,
    toggleSort,
  };
}
